import CodeQuizEditor from "@/components/storyboard/code/CodeQuizEditor";
import { CodeQuizWorkspace } from "@/components/storyboard/code/CodeQuizWorkspace";
import { CodeQuizActions } from "@/components/storyboard/quiz/CodeQuizActions";
import { codeQuizReview } from "@/components/storyboard/quiz/CodeQuizVerdict";
import { QuizChallengeHeading } from "@/components/storyboard/quiz/QuizChallengeHeading";
import {
  deriveDefaultLanguage,
  stripCodeFence,
} from "@/components/storyboard/code/codeUtils";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { ValidationSubmission } from "@/lib/validationSubmission";
import { useQuizCodeState } from "@/hooks/useQuizCodeState";
import type { QuizCodeDebugEntryData } from "@/types/storyboard";
import {
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizCodeDebugProps extends HiddenVerdictProps {
  entries: QuizCodeDebugEntryData[];
  /** Submits the corrected code for server-side (LLM) validation; also persists it. */
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
  /** Saved submissions per entry index, used to re-seed the editor on reopen. */
  initialSubmissions?: Record<number, ValidationSubmission>;
  /** Called after a submission is persisted, so saved state can be refreshed. */
  onSubmitted?: () => void;
}

/**
 * Code-debug quiz: the learner fixes broken code in an editor (seeded with the
 * buggy version) and submits it. "Code prüfen" runs server-side (LLM) validation
 * against the reference fix, which also persists the answer. Each task grants a
 * limited number of tries; no localStorage — durable state lives in
 * validation_data.
 */
const QuizCodeDebug = ({
  entries,
  onValidate,
  initialSubmissions,
  onSubmitted,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizCodeDebugProps) => {
  const defaultLanguage = deriveDefaultLanguage(
    entries.flatMap((e) => [e.codeWithBugs, e.solution ?? e.codeSolution])
  );

  const quiz = useQuizCodeState({
    entries,
    initialSubmissions,
    onValidate,
    onSubmitted,
    revealFeedback,
    onAdvance,
    getDefaultInput: (entry) => stripCodeFence(entry.codeWithBugs),
  });
  const {
    currentEntry,
    currentInput: currentCode,
    currentResult,
    isSettled,
    isValidating,
    handleInputChange: handleCodeChange,
  } = quiz;

  if (!entries || entries.length === 0) return null;
  if (!currentEntry) return null;

  const challenge = currentEntry.challenge ?? currentEntry.debugChallenge;

  const meta = (
    <>
      <QuizChallengeHeading challenge={challenge} />

      {currentEntry.errorMessage && (
        <div className="rounded-xl overflow-hidden border border-red-200">
          <div className="px-4 py-2 bg-red-50 border-b border-red-200">
            <span className="font-semibold text-red-600 uppercase tracking-wide">
              Fehlermeldung
            </span>
          </div>
          <div className="bg-red-50 px-4 py-3">
            <pre className="text-red-700 font-mono whitespace-pre-wrap break-all">
              {currentEntry.errorMessage}
            </pre>
          </div>
        </div>
      )}
    </>
  );

  const editor = (
    <CodeQuizEditor
      label="Dein korrigierter Code"
      value={currentCode}
      onChange={handleCodeChange}
      language={defaultLanguage}
      readOnly={isValidating || isSettled}
    />
  );

  const actions = (
    <CodeQuizActions
      quiz={quiz}
      submitLabel={
        collectsWithoutVerdict(revealFeedback)
          ? (advanceLabel ?? "Nächste Frage")
          : "Code prüfen"
      }
      canSubmit={currentCode.trim().length > 0}
    />
  );

  return (
    <CodeQuizWorkspace
      meta={meta}
      feedback={codeQuizReview(
        currentResult,
        "Nutze das Feedback und überarbeite deinen Code – er bleibt erhalten."
      )}
      editor={editor}
      actions={actions}
    />
  );
};

export default QuizCodeDebug;
